import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import {
  categoryName,
  formatCount,
  formatDate,
  getArticleById,
  getCategoryMeta,
  getRelatedArticles,
} from "@/utils/articles";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [toast, setToast] = useState(false);
  const article = id ? getArticleById(id) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(false), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  if (!article) {
    return (
      <SiteLayout>
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-28 text-center">
          <i className="ri-error-warning-line text-6xl text-foreground-300" />
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mt-6">
            文章不存在或已删除
          </h1>
          <p className="text-foreground-500 mt-3 text-sm">
            您访问的内容可能已被移除，或链接有误。
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-secondary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-secondary-600 transition-colors"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const meta = getCategoryMeta(article.category);
  const related = getRelatedArticles(article, 3);

  const copyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* clipboard unavailable */
    }
    setToast(true);
  };

  return (
    <SiteLayout>
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 pt-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: meta?.name ?? "文章", to: meta?.path },
            { label: article.title },
          ]}
        />
      </div>

      <article className="max-w-[820px] mx-auto px-4 md:px-8 pt-8 md:pt-12 pb-6">
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <Link
            to={meta?.path ?? "/"}
            className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium hover:bg-secondary-200 transition-colors"
          >
            {categoryName(article.category)}
          </Link>
          <span className="text-xs text-foreground-400">{formatDate(article.date)}</span>
        </div>

        <h1 className="font-heading text-2xl md:text-4xl font-bold leading-tight text-foreground-950 mb-6">
          {article.title}
        </h1>

        <div className="flex items-center flex-wrap gap-x-5 gap-y-2 pb-6 border-b border-background-200 text-sm text-foreground-500">
          <span className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-medium">
              {article.author.slice(0, 1)}
            </span>
            {article.author}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-eye-line" />
            {formatCount(article.views)} 阅读
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-bookmark-line" />
            {formatCount(article.favorites)} 收藏
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300 text-xs text-foreground-600 hover:border-secondary-400 hover:text-secondary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-link" />
              复制链接
            </button>
            <button
              onClick={copyLink}
              aria-label="分享"
              className="w-8 h-8 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-secondary-400 hover:text-secondary-700 transition-colors cursor-pointer"
            >
              <i className="ri-share-line" />
            </button>
          </div>
        </div>

        <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200 my-8">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 理论电影`}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="article-body">
          {article.content.map((block, i) => {
            if (block.type === "h2") {
              return (
                <h2
                  key={i}
                  className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4 flex items-center gap-3"
                >
                  <span className="w-1 h-6 bg-accent-500 rounded-full" />
                  {block.text}
                </h2>
              );
            }
            if (block.type === "quote") {
              return (
                <blockquote
                  key={i}
                  className="my-7 pl-5 border-l-4 border-secondary-400 bg-secondary-50 rounded-r-lg py-4 pr-4 font-heading text-base md:text-lg text-foreground-800 leading-relaxed"
                >
                  {block.text}
                </blockquote>
              );
            }
            if (block.type === "image") {
              return (
                <figure key={i} className="my-8">
                  <div className="w-full rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={block.src}
                      alt={block.alt}
                      title={`${block.alt} - 理论电影`}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                  <figcaption className="text-xs text-foreground-400 text-center mt-3">
                    {block.alt}
                  </figcaption>
                </figure>
              );
            }
            return (
              <p key={i} className="text-[15px] md:text-base text-foreground-700 leading-[1.9]">
                {block.text}
              </p>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-background-200">
          <span className="text-xs text-foreground-400 mr-1">标签：</span>
          {article.tags.map((t) => (
            <Link
              key={t}
              to={`/search?q=${encodeURIComponent(t)}`}
              className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-accent-100 hover:text-accent-900 transition-colors"
            >
              {t}
            </Link>
          ))}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-secondary-400 hover:text-secondary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            返回上一页
          </button>
          <Link
            to={meta?.path ?? "/"}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            更多{meta?.name}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-background-100 border-t border-background-200 mt-8">
          <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-14 md:py-16">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
              相关阅读
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {related.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[80] px-5 py-3 rounded-md bg-primary-900 text-background-50 text-sm shadow-lg animate-fade-up flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-secondary-400" />
          链接已复制到剪贴板
        </div>
      )}
    </SiteLayout>
  );
}